import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { FormulaCatalogoService } from '../../../../shared/services/apis/motor-fiscal/formula-catalogo.service';
import { FormulaModeloEmpresaService } from '../../../../shared/services/apis/motor-fiscal/formula-modelo-empresa.service';
import { RegraTributariaService } from '../../../../shared/services/apis/motor-fiscal/regra-tributaria.service';
import { RegraTributariaBulkDeleteRequest } from '../../../../shared/services/requests/motor-fiscal/regra-tributaria-bulk-delete.request';
import { FormulaCatalogoGrupoStruct } from '../../../../shared/services/structs/formula/formula-catalogo-grupo.struct';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { TabsScrollDirective } from '../../../../shared/directives/tabs-scroll.directive';
import { RegrasListaComponent } from './tabs/regras-lista/regras-lista.component';
import { FormulasRegraTributariaTabComponent } from './tabs/formulas-lista/formulas-lista.component';

@Component({
    selector: 'app-regra-tributaria-lista',
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        MatTabsModule,
        MatButtonModule,
        MatMenuModule,
        MatTooltipModule,
        MatDialogModule,
        TabsScrollDirective,
        HeaderComponent,
        RegrasListaComponent,
        FormulasRegraTributariaTabComponent,
    ],
    templateUrl: './regra-tributaria-lista.component.html',
    styleUrl: './regra-tributaria-lista.component.css',
})
export class RegraTributariaListaComponent {
    public moduloEnum = ModuloEnum.Configuracoes;
    public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_ReformaTributaria;

    public _gruposFormulas: FormulaCatalogoGrupoStruct[] = [];
    public _isLoadingFormulas: boolean = false;
    public _formulasCarregadas: boolean = false;

    @ViewChild(RegrasListaComponent) regrasListaComponent!: RegrasListaComponent;

    constructor(
        private formulaService: FormulaCatalogoService,
        private formulaModeloEmpresaService: FormulaModeloEmpresaService,
        private regraService: RegraTributariaService,
        private alertService: AlertService,
        private dialog: MatDialog,
        private router: Router
    ) {}

    public get selection() {
        return this.regrasListaComponent?.selection;
    }

    public podeExcluirSelecionados(): boolean {
        return !!this.selection && this.selection.hasValue();
    }

    public excluirSelecionados(): void {
        if (!this.selection || !this.selection.hasValue()) {
            return;
        }

        const selecionadas = this.selection.selected;
        const total = selecionadas.length;

        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmar Exclusão em Massa',
                description: `Confirma a exclusão de ${total} regra(s) tributária(s) selecionada(s)? Esta ação não poderá ser desfeita.`,
                buttonConfirm: 'Confirmar',
                buttonClose: 'Cancelar',
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.confirm) {
                const request: RegraTributariaBulkDeleteRequest = {
                    ids: selecionadas.map(item => item.idRegraTributaria)
                };
                this.regraService.bulkDelete(request).subscribe({
                    next: response => {
                        // A exclusão em massa é parcial: pode excluir parte da seleção e ainda
                        // retornar erro. Por isso a listagem recarrega em qualquer caso, para não
                        // deixar a tela mostrando regras que já foram excluídas.
                        this.regrasListaComponent.clickPesquisar();
                        if (response.isError) {
                            this.alertService.show('Atenção', response.errorDescription || 'Erro ao excluir selecionados', AlertType.warning);
                            return;
                        }
                        this.alertService.show('Sucesso', 'Regras tributárias excluídas com sucesso.', AlertType.success);
                    },
                    error: err => {
                        this.alertService.show('Erro inesperado', err.message || 'Erro ao excluir selecionados', AlertType.error);
                    }
                });
            }
        });
    }

    public novaRegra(): void {
        this.router.navigate(['/configuracoes/reforma-tributaria/regras/cadastrar']);
    }

    public voltar(): void {
        this.router.navigate(['/configuracoes/reforma-tributaria']);
    }

    public onTabFormulasAtivada(): void {
        if (this._formulasCarregadas) return;

        this._isLoadingFormulas = true;
        this.formulaService.getContexto('regra-tributaria').subscribe({
            next: response => {
                this._isLoadingFormulas = false;
                if (response.isError) {
                    this.alertService.show('Erro', response.errorDescription || 'Erro ao carregar fórmulas de Regra Tributária', AlertType.error);
                    return;
                }
                this._gruposFormulas = response.grupos;
                this._formulasCarregadas = true;
            },
            error: err => {
                this._isLoadingFormulas = false;
                this.alertService.show('Erro inesperado', err.message || 'Erro ao carregar catálogo de Regra Tributária', AlertType.error);
            },
        });
    }

    public alterarModeloEmpresa(selecao: { chaveFormula: string; chaveFormulaModelo: string }): void {
        this.formulaModeloEmpresaService.alterar(selecao.chaveFormula, selecao.chaveFormulaModelo).subscribe({
            next: response => {
                if (response.isError) {
                    this.alertService.show('Erro', response.errorDescription || 'Não foi possível alterar o modelo.', AlertType.error);
                    return;
                }
                this._formulasCarregadas = false;
                this.onTabFormulasAtivada();
                this.alertService.show('Sucesso', 'Modelo da fórmula atualizado.', AlertType.success);
            },
            error: () => this.alertService.show('Erro', 'Não foi possível alterar o modelo.', AlertType.error),
        });
    }
}
